import { getLanguage, getTranslations } from "../translations";
import { EligiblePlan } from "./eligibility";
import { style } from "./style";

type RenderOptions = {
  locale?: string;
  installmentsCount: number;
  isDeferredCapture: boolean;
  onPayButtonClicked?: () => void;
  containerStyle?: Partial<CSSStyleDeclaration>;
  buttonStyle?: Partial<CSSStyleDeclaration>;
};

function element<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string
) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

// Wrapped in a span so that it takes the height of a text line
function skeleton(width: number, lineClassName?: string) {
  const bar = element("span", "skeleton");
  bar.style.width = `${width}px`;
  const line = element("span", lineClassName);
  line.append(bar);
  return line;
}

// A merchant may pass anything, e.g. a CSS string, which must not break the rendering
function applyStyle(node: HTMLElement, style?: Partial<CSSStyleDeclaration>) {
  if (style && typeof style === "object") Object.assign(node.style, style);
}

function createContainer(options: RenderOptions) {
  const container = element("div", "container fadeIn");
  applyStyle(container, options.containerStyle);
  return container;
}

let styleSheet: CSSStyleSheet | null = null;

/**
 * A constructed stylesheet is not blocked by a merchant's CSP without 'unsafe-inline',
 * unlike a <style> element, kept for browsers without adoptedStyleSheets (Safari < 16.4)
 */
function show(root: ShadowRoot, container: HTMLElement) {
  if ("adoptedStyleSheets" in root && "replaceSync" in CSSStyleSheet.prototype) {
    if (!styleSheet) {
      styleSheet = new CSSStyleSheet();
      styleSheet.replaceSync(style);
    }
    root.adoptedStyleSheets = [styleSheet];
    root.replaceChildren(container);
  } else {
    root.replaceChildren(element("style", undefined, style), container);
  }
}

/**
 * Breakpoints of the former checkout iframe, whose viewport was the container.
 * Applied again on resize: the host may be hidden (width 0) when rendered, e.g. in a collapsed accordion
 */
export function applyWidth(root: ShadowRoot, locale?: string) {
  const width = root.host.clientWidth;
  root.querySelector(".button")?.classList.toggle("fitContent", width >= 769);

  const formatDate = new Intl.DateTimeFormat(getLanguage(locale), {
    day: "numeric",
    month: width > 400 ? "long" : "short",
  }).format;
  root.querySelectorAll<HTMLElement>(".date").forEach((date) => {
    date.textContent = formatDate(Number(date.dataset.dueDate));
  });
}

function isCredit(installmentsCount: number) {
  return installmentsCount > 4;
}

/**
 * Same layout as the payment plan, so that the height does not change when it loads
 */
export function renderSkeleton(root: ShadowRoot, options: RenderOptions) {
  const container = createContainer(options);
  container.classList.add("delayed");
  show(root, container);
  const t = getTranslations(options.locale).embedded;

  if (options.isDeferredCapture) {
    container.append(element("div", "bold", t.deferredCaptureExplanation));
  }

  if (!isCredit(options.installmentsCount)) {
    const installments = element("div", "installments");
    for (let index = 0; index < options.installmentsCount; index++) {
      const row = element("div", "installment");
      row.append(skeleton(90, "label"), skeleton(60));
      installments.append(row);
    }

    const total = element("div", "total");
    const totalAmount = element("div", "totalAmount");
    const totalSkeleton = element("div", "bold");
    totalSkeleton.append(skeleton(60));
    totalAmount.append(totalSkeleton, element("div", "fees", "\u00a0"));
    total.append(skeleton(40), totalAmount);
    installments.append(total);
    container.append(installments);
  }

  if (options.onPayButtonClicked) {
    const button = element("button", "button skeletonButton", "\u00a0");
    button.disabled = true;
    applyStyle(button, options.buttonStyle);
    container.append(button);
  }

  applyWidth(root, options.locale);
}

export function renderPaymentPlan(
  root: ShadowRoot,
  plan: EligiblePlan,
  options: RenderOptions
) {
  const container = createContainer(options);

  const language = getLanguage(options.locale);
  const t = getTranslations(options.locale).embedded;
  const formatAmount = (cents: number) =>
    new Intl.NumberFormat(language, {
      style: "currency",
      currency: "EUR",
    }).format(cents / 100);

  if (options.isDeferredCapture) {
    container.append(element("div", "bold", t.deferredCaptureExplanation));
  }

  const customerFee = plan.customer_fee + plan.customer_interest;
  const totalAmount = plan.purchase_amount + customerFee;

  if (!isCredit(options.installmentsCount)) {
    const installments = element("div", "installments");
    // Same test ids as the former checkout iframe
    installments.dataset.testid = "installments";

    plan.payment_plan.forEach((installment, index) => {
      const row = element("div", "installment");
      row.dataset.testid = `installment-${index}`;

      const label = element("span", "label");
      if (options.isDeferredCapture) {
        label.textContent =
          index === 0 ? t.deferredFirstInstallment : t.deferredMonth(index);
      } else {
        // Formatted by `applyWidth`
        label.classList.add("date");
        label.dataset.dueDate = String(installment.due_date * 1000);
      }

      row.append(
        label,
        element(
          "span",
          undefined,
          formatAmount(
            installment.purchase_amount +
              installment.customer_fee +
              installment.customer_interest
          )
        )
      );
      installments.append(row);
    });

    const total = element("div", "total");
    const fees = element("div", "fees");
    if (customerFee === 0) {
      const gift = element("span", undefined, "🎁");
      gift.setAttribute("aria-hidden", "true");
      fees.append(`${t.offeredFees}\u00a0`, gift);
    } else {
      fees.textContent = t.fees(formatAmount(customerFee));
    }
    const totalRight = element("div", "totalAmount");
    totalRight.append(element("div", "bold", formatAmount(totalAmount)), fees);
    total.append(element("span", "bold", t.total), totalRight);
    installments.append(total);
    container.append(installments);
  }

  if (options.onPayButtonClicked) {
    const button = element(
      "button",
      "button",
      t.payButton(formatAmount(totalAmount))
    );
    button.type = "button";
    applyStyle(button, options.buttonStyle);
    button.onclick = () => options.onPayButtonClicked?.();
    container.append(button);
  }

  show(root, container);
  applyWidth(root, options.locale);
}

export function renderError(root: ShadowRoot, options: RenderOptions) {
  const container = createContainer(options);
  container.classList.add("error");
  container.textContent = getTranslations(options.locale).embedded.noPlanError;
  show(root, container);
}
